/**
 * SKADE COP — inngang. Autentisering og ruting; kartet og panelene lastes
 * med dynamisk import() så innloggingen ikke parser MapLibre/ORBAT/intel.
 */
import './style.css';
import './utseende-v2.css';
import type { User } from '@skade/rbac';
import type { MeUser } from './auth/api.js';
import { standalonePanelId } from './panels/panel-rute.js';
import { demoUser } from './session.js';
import { settKanFjerneAndresEnheter, settPrototypeAapen } from './auth/fullmakter.js';

// UX-030: «Utseende v2» er standard (eier 2026-10-21: «Alle fasene på UI/UX er nå
// klare»). Settes før noe rendres, også i pop-out-vinduer og vegg-kartet (samme
// inngang). `utseende-v2.css` er fortsatt scopet til `:root[data-utseende="v2"]`
// til reglene er brettet inn i style.css (VALE UX-031).
document.documentElement.dataset.utseende = 'v2';

// VALE I18N-01: `lang` og fanetittelen følger brukerens språk. Dynamisk import, så
// innloggingens statiske graf forblir tynn (katalogene følger med uansett i neste
// steg). Til den er lastet står nb fra index.html.
void import('./i18n/index.js').then(({ aktivSprak, t }) => {
  document.documentElement.lang = aktivSprak();
  document.title = t('boot.tittel');
});

const app = document.getElementById('app');
if (!app) throw new Error('#app mangler');
const root: HTMLElement = app;

// Ekte backend? Uten VITE_SKADE_WS kjører demo-feed → vis DEMO-merke, hopp
// over innlogging (frittstående UI-visning uten backend).
const wsUrl = import.meta.env.VITE_SKADE_WS as string | undefined;

function startCop(user: User, demo: boolean): Promise<void> {
  return import('./boot-app.js').then((m) => {
    m.bootApp(root, user, demo, wsUrl);
  });
}

function startPanel(id: string, user: User): void {
  void import('./panels/boot-panel.js').then((m) => {
    void m.bootPanel({ root, id, user, wsUrl });
  });
}

/**
 * Live-sti: hjemtjener + sesjon. Dynamisk import så demo-COP ikke parser
 * Zod/kontrakter eller innloggingsskjermen.
 *
 * ADR-002: en konto med engangspassord får passordbyttet FØR kartet — også ved
 * F5 med gyldig cookie. `showLogin` gjør det samme rett etter innlogging.
 */
async function medSesjon(videreUtenFlagg: (u: User) => void): Promise<void> {
  // BRUK-08: bryteren følger /auth/me og innloggingssvaret — sett den før
  // noe panel leser den, uansett hvilken vei brukeren kom inn. I18N-01: språket
  // fra profilen (valg > ORBAT-land) settes før COP tegnes; høyst 1,5 s ventetid.
  const videre = (u: MeUser) => { settPrototypeAapen(u.prototypeAapen === true); settKanFjerneAndresEnheter(u.kanFjerneAndresEnheter === true); void import('./i18n/hub-sprak.js').then((m) => m.lastSprakFraHub()).catch(() => undefined).finally(() => videreUtenFlagg(u));
    // Kildeland (DATALAG-DK-01) — venter ikke på svaret; kartlaget lytter og henter på nytt.
    void import('./map/kildeland.js').then((m) => m.lastKildelandFraHub()).catch(() => undefined); };
  const [{ lastGnaKonfig }, { fetchMe }, { showLogin, showPassordbytte }] = await Promise.all([
    import('./gna-konfig.js'),
    import('./auth/api.js'),
    import('./auth/login-view.js'),
  ]);
  await lastGnaKonfig();
  const user = await fetchMe();
  if (!user) {
    showLogin(root, videre);
    return;
  }
  if (user.maaByttePassord) showPassordbytte(root, user, videre);
  else videre(user);
}

const panelId = standalonePanelId();
if (panelId) {
  const boot = (u: User) => startPanel(panelId, u);
  if (!wsUrl) boot(demoUser);
  else void medSesjon(boot);
} else if (!wsUrl) {
  startCop(demoUser, true);
} else {
  void medSesjon((u) => startCop(u, false));
}
